import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Button } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import Icon from '../../../../shared/components/button/enums/Icon';
import IconRender from '../../../../shared/components/icon/IconRender';
import useSurveyContext from '../../../survey/store/UseSurveyContext';
import useResultContext from '../../store/UseResultContext';
import { SurveyQuestionCategory } from '../../types';
import HabitRenderer from '../habit/HabitRenderer';
import StateChip from '../stateChip/StateChips';
import CategoryEnum from './enums/CatergoryEnum';

type CategoryProps = {
  category: SurveyQuestionCategory;
  setQuestionAnswer: (questionAnswer: any) => void;
  showNotes: boolean;
  description?: string;
};

const Category = ({
  category: { category, questions },
  setQuestionAnswer,
  showNotes = false,
  description = '',
}: CategoryProps) => {
  const { bemClassName, bemClassNames } = useBem('dashboard');
  const { getThemeName, getLink } = useSurveyContext();
  const location = useLocation();
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState<boolean>(false);

  const {
    result: { topCompliments, topImprovements },
  } = useResultContext();

  const isCompliments = topCompliments?.some((item) => item === category) ?? false;
  const isImprovements = topImprovements?.some((item) => item === category) ?? false;

  const getGridLayoutClass = (categoryName: CategoryEnum): string => {
    const layoutMapping: Record<CategoryEnum, string> = {
      [CategoryEnum.Weight]: 'grid-layout-numericInput',
      [CategoryEnum.Sleep]: 'grid-layout-numericInput',
      [CategoryEnum.Smoking]: 'grid-layout-numericInput',
      [CategoryEnum.Exercise]: 'grid-layout-numericInput',
      [CategoryEnum.Coffee]: 'grid-layout-numericInput',
      [CategoryEnum.Nutrition]: 'grid-layout-nutrition',
      [CategoryEnum.Stress]: 'grid-layout-stress',
      [CategoryEnum.Alcohol]: 'default-grid-layout',
      [CategoryEnum.Vitals]: 'default-grid-layout',
      [CategoryEnum.Cholesterol]: 'default-grid-layout',
    };
    return layoutMapping[categoryName] || 'default-grid-layout';
  };

  const categoryImages: Record<string, string> = {
    [CategoryEnum.Nutrition]: '/images/voeding_bron.jpg',
    [CategoryEnum.Weight]: '/images/gewicht_bron.jpg',
    [CategoryEnum.Coffee]: '/images/koffie_bron.jpg',
    [CategoryEnum.Sleep]: '/images/slapen_bron.jpg',
    [CategoryEnum.Smoking]: '/images/roken_bron.jpg',
    [CategoryEnum.Exercise]: '/images/bewegen_bron.jpg',
    [CategoryEnum.Cholesterol]: '/images/cholesterol_bron.jpg',
  };

  const categoryIconMap: Record<CategoryEnum, Icon> = {
    [CategoryEnum.Nutrition]: Icon.voeding,
    [CategoryEnum.Weight]: Icon.gewicht,
    [CategoryEnum.Coffee]: Icon.koffie,
    [CategoryEnum.Sleep]: Icon.slapen,
    [CategoryEnum.Smoking]: Icon.roken,
    [CategoryEnum.Exercise]: Icon.beweging,
    [CategoryEnum.Cholesterol]: Icon.cholesterol,
    [CategoryEnum.Stress]: Icon.stress,
    [CategoryEnum.Alcohol]: Icon.alcohol,
    [CategoryEnum.Vitals]: Icon.vitalewaarden,
  };

  const getCategoryIcon = (categoryName: string): Icon => categoryIconMap[categoryName as CategoryEnum];

  const getDisplayLimit = (categoryName: string): number => {
    switch (categoryName) {
      case CategoryEnum.Stress:
        return 3;
      default:
        return questions.length;
    }
  };

  const gridLayoutClass = getGridLayoutClass(category as CategoryEnum);
  const displayLimit = getDisplayLimit(category);
  const questionsToShow = showAll ? questions : questions.slice(0, displayLimit);
  const hasMoreItems = questions.length > displayLimit;

  return (
    <div
      key={category}
      className={bemClassName('habits-container')}
    >
      <div className={bemClassName('habits-container__header')}>
        <div className={bemClassName('habits-container__header__icon')}>
          <div
            className={bemClassNames([
              bemClassName('habits-container__header__icon__container'),
              bemClassName('habits-container__header__icon__container', 'desktop'),
            ])}
          >
            <IconRender
              icon={getCategoryIcon(category)}
              width="16"
              height="20"
              color="var(--zk-dark-blue)"
            />
          </div>
          <div
            className={bemClassNames([
              bemClassName('habits-container__header__icon__container'),
              bemClassName('habits-container__header__icon__container', 'mobile'),
            ])}
          >
            <IconRender
              icon={getCategoryIcon(category)}
              width="16"
              height="20"
              color="var(--zk-dark-blue)"
            />
          </div>
          <h2 className={bemClassName('habits-container__header__title')}>
            {t(`dashboard.categories.${category}`, getThemeName(category))}
          </h2>

          {(isCompliments || isImprovements) && (
            <div className={bemClassName('habits-container__header__status')}>
              <StateChip isCompliment={isCompliments} />
            </div>
          )}
        </div>
        {showNotes && description && <p className={bemClassName('habits-container__notes')}>{description}</p>}
        <Link
          to={getLink(category)}
          state={{ from: location.pathname }}
          id={`theme-header-link-${category}`}
        >
          <Button>
            {t('dashboard.read-more')}
            <IconRender
              icon={Icon.arrowRight}
              color="currentColor"
            />
          </Button>
        </Link>
      </div>
      <div className={bemClassNames([bemClassName('habits'), bemClassName('habits', gridLayoutClass)])}>
        {questionsToShow.map((question) => (
          <HabitRenderer
            key={question.questionId}
            question={question}
            setQuestionAnswer={setQuestionAnswer}
          />
        ))}
        {categoryImages[category as CategoryEnum] && (
          <div className="dashboard__habits-image-placeholder">
            <img
              src={categoryImages[category as CategoryEnum]}
              alt={category}
            />
          </div>
        )}
      </div>

      {hasMoreItems && (
        <div className={bemClassName('habits-container__footer')}>
          {!showAll ? (
            <Button onClick={() => setShowAll(true)}>
              <IconRender
                icon={Icon.chevron}
                width="16"
                height="20"
                color="var(--zk-dark-blue)"
              />
              {t('dashboard.show-more')}
            </Button>
          ) : (
            <Button onClick={() => setShowAll(false)}>
              <IconRender
                icon={Icon.chevron}
                width="16"
                height="20"
                color="var(--zk-dark-blue)"
                className="chevron-rotated"
              />
              {t('dashboard.show-less')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Category;
